/**
 * BMS: mapování telemetrie → MQTT topic suffix. Per-cell topicy (cell-01…cell-NN) jsou DYNAMICKÉ
 * (počet = délka voltagesMv) → mimo statickou mapu (bmsCellTopics). Pořadí klíčů = pořadí původních
 * publish volání (bms/index.ts). Settings (cell-ovp/uvp) jen když je settings != null.
 */

import type { BmsSettingsShape, BmsTelemetryShape, MetricValue, TopicMap } from "@globals/telemetry/types";

const fixed = (digits: number) => (v: MetricValue) => (v as number).toFixed(digits);
/** deci°C → °C s null-guardem PŘED dělením (null/10===0 by byl bug). */
const deci = (v: number | null): number | null => (v === null ? null : v / 10);

export const BMS_TOPIC_MAP: TopicMap<BmsTelemetryShape> = {
  voltage: { get: (t) => t.pack.packVoltageMv / 1000, format: fixed(3) },
  current: { get: (t) => t.pack.packCurrentMa / 1000, format: fixed(3) },
  power: { get: (t) => t.pack.powerMw / 1000, format: fixed(2) },
  soc: { get: (t) => t.pack.socPercent },
  "remaining-mah": { get: (t) => t.pack.remainingCapacityMah },
  "cycle-count": { get: (t) => t.pack.cycleCount },
  "cycle-capacity-ah": { get: (t) => t.pack.cycleCapacityAh, format: fixed(2) },
  "temp-t1": { get: (t) => deci(t.pack.temperatureT1DeciC), cond: (t) => t.pack.temperatureT1DeciC !== null, format: fixed(1) },
  "temp-t2": { get: (t) => deci(t.pack.temperatureT2DeciC), cond: (t) => t.pack.temperatureT2DeciC !== null, format: fixed(1) },
  "temp-mos": { get: (t) => deci(t.pack.temperatureMosDeciC), cond: (t) => t.pack.temperatureMosDeciC !== null, format: fixed(1) },
  "temp-t4": { get: (t) => deci(t.pack.temperatureT4DeciC), cond: (t) => t.pack.temperatureT4DeciC !== null, format: fixed(1) },
  "temp-t5": { get: (t) => deci(t.pack.temperatureT5DeciC), cond: (t) => t.pack.temperatureT5DeciC !== null, format: fixed(1) },
  "temp-t6": { get: (t) => deci(t.pack.temperatureT6DeciC), cond: (t) => t.pack.temperatureT6DeciC !== null, format: fixed(1) },
  "cell-avg": { get: (t) => t.cells.avgCellMv },
  "cell-delta": { get: (t) => t.cells.cellDeltaMv },
  alarms: { get: (t) => t.alarms.raw },
  "charge-mosfet": { get: (t) => t.mosfet.chargeOn },
  "discharge-mosfet": { get: (t) => t.mosfet.dischargeOn },
  balancing: { get: (t) => t.mosfet.balancing },
  "charge-mosfet-status": { get: (t) => t.mosfet.chargeStatusCode },
  "discharge-mosfet-status": { get: (t) => t.mosfet.dischargeStatusCode },
  "balancer-status": { get: (t) => t.mosfet.balancerStatusCode },
};

export const BMS_SETTINGS_MAP: TopicMap<BmsSettingsShape> = {
  "cell-ovp": { get: (s) => s.cellOvpMv },
  "cell-uvp": { get: (s) => s.cellUvpMv },
};

/** Dynamické per-cell topicy: cell-01…cell-NN (1-based, 2místné padding) = napětí článku v mV. */
export const bmsCellTopics = (t: BmsTelemetryShape): Array<[string, string]> =>
  t.cells.voltagesMv.map((mv, idx) => [`cell-${String(idx + 1).padStart(2, "0")}`, String(mv)]);

/** Regex pro per-cell suffix (cell-01…); skupina = 1-based index. */
export const BMS_CELL_SUFFIX_RE = /^cell-(\d{2})$/;

/** Živá hodnota jednoho článku (1-based index z cell-NN), nebo null mimo rozsah. */
export const bmsCellValue = (t: BmsTelemetryShape, oneBasedIndex: number): number | null =>
  t.cells.voltagesMv[oneBasedIndex - 1] ?? null;
